import { captureUsTime, usRequestStamp, type UsTimeEvidence } from './timeEvidence';
import { isUsGameSport, usPeriodForMarketType, type UsGameSport, type UsPeriod } from './sports';
import { UsClientError, type UsClientSession } from './tradingClient';
import type { UsScope } from './types';
import { usGamePropFamily, validUsGamePropLine } from './gamePropFamily';
import { usPlayerFamily, usPlayerMetadata, type UsPlayerMetadata } from './playerFamily';

export interface UsDiscoveryMarket {
  timeEvidence?: UsTimeEvidence;
  marketSlug: string; marketId: string; sport: UsGameSport; title: string | null;
  gameKey: string; yesTeam: string | null; marketType: string; sportsMarketType: string;
  period?: UsPeriod | null;
  playerProp?: UsPlayerMetadata | null;
  /** Spread strike for the YES team as exact text ("-3.5": YES team wins by more
   * than 3.5); null for a moneyline or when the producer sent none. */
  line: string | null;
  classification: { revision: string; observedAtMs: number; expiresAtMs: number };
  book: { state: 'current' | 'stale' | 'unavailable'; observedAtMs: number | null; expiresAtMs: number | null;
    yesBidUsd: string | null; yesAskUsd: string | null; noBidUsd: string | null; noAskUsd: string | null };
}
export interface UsMarketPage {
  scope: UsScope; generation: string; observedAtMs: number;
  markets: UsDiscoveryMarket[]; nextCursor: string | null;
}
export interface UsSelectedMarket {
  timeEvidence?: UsTimeEvidence;
  scope: UsScope; observedAtMs: number; market: UsDiscoveryMarket; feedGeneration: string;
  rules: { venue: 'polymarket_us'; marketSlug: string; revision: string; sport: UsGameSport; active: boolean;
    priceTick: string; minimumQuantity: string; quantityStep: string; timeInForce: string[]; supportsPostOnly: boolean;
    observedAtMs: number; expiresAtMs: number };
  display: { question: string | null; description: string | null; yesLabel: string | null; noLabel: string | null };
}
/** Catalogue page from the account-free public read: the same market evidence, no account or scope. */
export type UsPublicMarketPage = Omit<UsMarketPage, 'scope'>;
export interface UsMarketSelection { accountId: string; scope: UsScope }
/** One aggregated resting bid (exact USD price and contract quantity). */
export interface UsBookLevel { priceUsd: string; size: string }
export interface UsBookSide { bids: UsBookLevel[]; complete: boolean; truncated: boolean }
/** Display depth for one market; never an execution input. */
export interface UsMarketBookView {
  timeEvidence?: UsTimeEvidence;
  scope: UsScope; observedAtMs: number; marketSlug: string;
  state: 'current' | 'stale' | 'unavailable'; bookObservedAtMs: number | null; bookExpiresAtMs: number | null;
  yes: UsBookSide; no: UsBookSide;
}
/** Depth from the account-free public read: display only, no account or scope. */
export type UsMarketBookData = Omit<UsMarketBookView, 'scope'>;
type UsCatalogueQuery = { q?: string; sport?: UsGameSport; after?: string; marketType?: string; slugs?: readonly string[] };
const US_BOOK_LEVEL_LIMIT = 25;
interface Dependencies {
  clock?: () => number;
  session(): UsClientSession | null;
  send(path: string, init: RequestInit, session: UsClientSession): Promise<Response>;
}
const fail = (code = 'US_CLIENT_INVALID_RESPONSE'): never => { throw new UsClientError(code); };
const uuid = (value: unknown): value is string => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(value);
const slug = (value: unknown): value is string => typeof value === 'string' && /^[A-Za-z0-9_-]{1,256}$/.test(value);
const hash = (value: unknown): value is string => typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
const record = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : fail();
const text = (value: unknown, max = 1000): string => typeof value === 'string' && value.trim()
  && value.length <= max && !/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(value) ? value : fail();
const label = (value: unknown, max = 1000): string | null => value === null ? null : text(value, max);
const time = (value: unknown): number => Number.isSafeInteger(value) && Number(value) >= 0 ? Number(value) : fail();
const decimal = (value: unknown): string => typeof value === 'string' && /^(?:0|[1-9]\d{0,11})(?:\.\d{1,12})?$/.test(value) ? value : fail();
const price = (value: unknown): string | null => value === null ? null : Number(decimal(value)) <= 1 ? String(value) : fail();
// Absent (an older relay) reads as no strike; anything else must be exact.
const strikeLine = (value: unknown): string | null => value === undefined || value === null ? null
  : typeof value === 'string' && /^-?[0-9]{1,4}(?:\.[0-9]{1,2})?$/.test(value) ? value : fail();
const same = (a: UsClientSession | null, b: UsClientSession) => a?.userId === b.userId && a.orgId === b.orgId && a.generation === b.generation;
const tifs = ['TIME_IN_FORCE_GOOD_TILL_CANCEL','TIME_IN_FORCE_GOOD_TILL_DATE','TIME_IN_FORCE_IMMEDIATE_OR_CANCEL','TIME_IN_FORCE_FILL_OR_KILL'];

function market(value: unknown, observed: number): UsDiscoveryMarket {
  const r = record(value), c = record(r.classification), b = record(r.book);
  if (!slug(r.marketSlug) || typeof r.marketId !== 'string' || !/^[0-9]{1,32}$/.test(r.marketId)
    || (!isUsGameSport(r.sport)) || !hash(c.revision)
    || !['current','stale','unavailable'].includes(String(b.state))) return fail();
  const classification = { revision: c.revision, observedAtMs: time(c.observedAtMs), expiresAtMs: time(c.expiresAtMs) };
  const book = { state: b.state as UsDiscoveryMarket['book']['state'], observedAtMs: b.observedAtMs === null ? null : time(b.observedAtMs),
    expiresAtMs: b.expiresAtMs === null ? null : time(b.expiresAtMs),
    yesBidUsd: price(b.yesBidUsd), yesAskUsd: price(b.yesAskUsd), noBidUsd: price(b.noBidUsd), noAskUsd: price(b.noAskUsd) };
  if (classification.observedAtMs > observed || classification.expiresAtMs <= classification.observedAtMs) return fail();
  if (book.state === 'unavailable') {
    if (book.observedAtMs !== null || book.expiresAtMs !== null
      || [book.yesBidUsd,book.yesAskUsd,book.noBidUsd,book.noAskUsd].some(v => v !== null)) return fail();
  } else if (book.observedAtMs === null || book.expiresAtMs === null || book.observedAtMs > observed
    || book.expiresAtMs <= book.observedAtMs || (book.state === 'current' && (book.expiresAtMs <= observed || classification.expiresAtMs <= observed))) return fail();
  const gameKey = text(r.gameKey,256);
  if (!gameKey.startsWith(r.sport.toLowerCase()+'|')) return fail();
  const sportsMarketType = text(r.sportsMarketType,256);
  const playerFamily = usPlayerFamily(sportsMarketType, r.sport);
  const playerProp = playerFamily ? usPlayerMetadata(sportsMarketType, r.playerProp, gameKey) : null;
  const gameFamily = usGamePropFamily(sportsMarketType, r.sport);
  const period = playerFamily || gameFamily ? 'full' : usPeriodForMarketType(sportsMarketType);
  const line = strikeLine(r.line);
  if (playerFamily && (!playerProp || r.marketType !== 'props' || r.yesTeam !== null
    || (playerFamily.kind === 'threshold' ? line === null || Number(line) < 0 : line !== null))) return fail();
  if (gameFamily && (r.marketType !== 'props' || r.yesTeam !== null || r.playerProp != null
    || !validUsGamePropLine(sportsMarketType, r.sport, line))) return fail();
  if (!playerFamily && r.playerProp != null) return fail();
  if (r.period !== undefined && r.period !== period) return fail();
  return { marketSlug: r.marketSlug, marketId: r.marketId, sport: r.sport, title: label(r.title),
    gameKey, yesTeam: label(r.yesTeam,256), marketType: text(r.marketType,256), sportsMarketType, period,
    line, ...(playerProp ? { playerProp } : {}), classification, book };
}

/** Read-only market client. Latest query/selection wins, including A-B-A.
 * Construction and selection start no request, polling, producer or write. */
export class UsMarketClient {
  private generation = 0;
  private selected: UsMarketSelection | null = null;
  constructor(private readonly d: Dependencies) {}
  invalidate(): void { this.generation++; this.selected = null; }
  setSelection(value: UsMarketSelection): void {
    this.invalidate();
    if (!uuid(value.accountId) || value.scope.venue !== 'polymarket_us' || !hash(value.scope.scopeKey) || !hash(value.scope.revision))
      return fail('US_CLIENT_INVALID_REQUEST');
    this.selected = { accountId: value.accountId, scope: { ...value.scope } };
  }
  /** One market-evidence GET. With a selection it is the account-scoped read and
   * must echo that account and scope; without one it is the account-free public
   * read and must carry neither. Latest read on this instance wins. */
  private async fetch(suffix: string, selected: UsMarketSelection | null, signal?: AbortSignal) {
    const current = this.d.session();
    if (!current || !uuid(current.userId) || !uuid(current.orgId) || !Number.isSafeInteger(current.generation)
      || current.generation < 0) return fail('US_CLIENT_AUTH_REQUIRED');
    const session = { ...current }, selection = selected && { accountId: selected.accountId, scope: { ...selected.scope } };
    const generation = ++this.generation;
    const assertCurrent = () => { if (generation !== this.generation || !same(this.d.session(),session)) fail('US_CLIENT_CONTEXT_CHANGED'); };
    const started = usRequestStamp(this.d.clock);
    try {
      const response = await this.d.send(`/api/polymarket-us${selection ? `/accounts/${selection.accountId}` : ''}/markets${suffix}`,
        { method:'GET',cache:'no-store',redirect:'error',signal:signal ?? AbortSignal.timeout(15_000) },session);
      assertCurrent(); const body = record(await response.json()); assertCurrent();
      if (!response.ok) return fail(typeof body.code === 'string' && /^US_[A-Z0-9_]{1,100}$/.test(body.code) ? body.code : 'US_MARKET_UNAVAILABLE');
      const timeEvidence = captureUsTime(time(body.observedAtMs), started, this.d.clock);
      if (!selection) {
        // Public evidence never names an account; one that does is not this read.
        if (body.venue !== 'polymarket_us' || body.source !== 'market_evidence'
          || body.accountId !== undefined || body.scope !== undefined) return fail();
        return { body, timeEvidence, scope: null, observedAtMs: time(body.observedAtMs) };
      }
      const scope = record(body.scope);
      if (body.venue !== 'polymarket_us' || body.source !== 'market_evidence' || body.accountId !== selection.accountId
        || scope.venue !== selection.scope.venue || scope.scopeKey !== selection.scope.scopeKey || scope.revision !== selection.scope.revision)
        return fail('US_SCOPE_CHANGED');
      return { body, timeEvidence, scope: selection.scope, observedAtMs: time(body.observedAtMs) };
    } catch (error) {
      assertCurrent(); if (error instanceof UsClientError) throw error;
      return fail('US_MARKET_UNAVAILABLE');
    }
  }
  private async catalogue(query: UsCatalogueQuery, selected: UsMarketSelection | null, signal?: AbortSignal) {
    if (query.slugs !== undefined && (!Array.isArray(query.slugs) || query.slugs.length < 1 || query.slugs.length > 20
      || query.slugs.some(s => !slug(s)) || new Set(query.slugs).size !== query.slugs.length
      || query.q !== undefined || query.sport !== undefined || query.after !== undefined || query.marketType !== undefined))
      return fail('US_CLIENT_INVALID_REQUEST');
    if ((query.q !== undefined && (query.q.length > 120 || /[\u0000-\u001f\u007f]/.test(query.q)))
      || (query.sport !== undefined && !isUsGameSport(query.sport))
      || (query.after !== undefined && !/^[A-Za-z0-9_-]{1,2000}$/.test(query.after))
      || (query.marketType !== undefined && !/^[a-z_]{1,64}$/.test(query.marketType))) return fail('US_CLIENT_INVALID_REQUEST');
    const parameters = new URLSearchParams();
    if (query.q !== undefined) parameters.set('q',query.q);
    if (query.sport !== undefined) parameters.set('sport',query.sport);
    if (query.after !== undefined) parameters.set('after',query.after);
    if (query.marketType !== undefined) parameters.set('marketType',query.marketType);
    if (query.slugs !== undefined) parameters.set('slugs',query.slugs.join(','));
    const { body, scope, observedAtMs, timeEvidence } = await this.fetch(parameters.size ? '?'+parameters.toString() : '', selected, signal);
    if (!Array.isArray(body.markets) || body.markets.length > 100
      || !(body.nextCursor === null || (typeof body.nextCursor === 'string' && /^[A-Za-z0-9_-]{1,2000}$/.test(body.nextCursor)))) return fail();
    const markets = body.markets.map(r => ({ ...market(r,observedAtMs), timeEvidence }));
    if (query.slugs !== undefined && (body.nextCursor !== null || markets.some(m => !query.slugs!.includes(m.marketSlug)))) return fail();
    if (new Set(markets.map(r=>r.marketSlug)).size !== markets.length || new Set(markets.map(r=>r.marketId)).size !== markets.length) return fail();
    return { scope, observedAtMs, generation: text(body.generation,256), markets, nextCursor: body.nextCursor as string | null };
  }
  async listMarkets(query: UsCatalogueQuery = {}): Promise<UsMarketPage> {
    const selected = this.selected;
    if (!selected) return fail('US_CLIENT_AUTH_REQUIRED');
    const page = await this.catalogue(query, selected);
    return { ...page, scope: { ...selected.scope } };
  }
  /** Account-free catalogue read (`GET /api/polymarket-us/markets`): the same
   * market evidence any signed-in user may see, for display only (as with
   * Kalshi/Polymarket market data). No account, scope or terms. */
  async listPublicMarkets(query: UsCatalogueQuery = {}, signal?: AbortSignal): Promise<UsPublicMarketPage> {
    const { observedAtMs, generation, markets, nextCursor } = await this.catalogue(query, null, signal);
    return { observedAtMs, generation, markets, nextCursor };
  }
  /** Selected-market depth. Use a dedicated client instance for polling: every
   * read supersedes earlier reads on the same instance (latest wins). */
  async readBook(marketSlug: string): Promise<UsMarketBookView> {
    if (!slug(marketSlug)) return fail('US_CLIENT_INVALID_REQUEST');
    const selected = this.selected;
    if (!selected) return fail('US_CLIENT_AUTH_REQUIRED');
    return { scope: { ...selected.scope }, ...await this.depth(marketSlug, selected) };
  }
  /** Account-free display depth (`GET /api/polymarket-us/markets/:slug/book`);
   * never an execution input. Same latest-wins rule as readBook. */
  async readPublicBook(marketSlug: string): Promise<UsMarketBookData> {
    if (!slug(marketSlug)) return fail('US_CLIENT_INVALID_REQUEST');
    return this.depth(marketSlug, null);
  }
  private async depth(marketSlug: string, selected: UsMarketSelection | null): Promise<UsMarketBookData> {
    const { body, observedAtMs, timeEvidence } = await this.fetch('/'+marketSlug+'/book', selected);
    const b = record(body.book);
    if (b.marketSlug !== marketSlug || !['current','stale','unavailable'].includes(String(b.state))) return fail();
    const bookObservedAtMs = b.observedAtMs === null ? null : time(b.observedAtMs);
    const bookExpiresAtMs = b.expiresAtMs === null ? null : time(b.expiresAtMs);
    if ((bookObservedAtMs === null) !== (bookExpiresAtMs === null)
      || (bookObservedAtMs !== null && (bookObservedAtMs > observedAtMs || bookExpiresAtMs! <= bookObservedAtMs))) return fail();
    const side = (value: unknown): UsBookSide => {
      const r = record(value);
      if (typeof r.complete !== 'boolean' || typeof r.truncated !== 'boolean' || !Array.isArray(r.bids)
        || r.bids.length > US_BOOK_LEVEL_LIMIT || (!r.complete && (r.bids.length || r.truncated))) return fail();
      const bids = r.bids.map((level): UsBookLevel => {
        const l = record(level), p = decimal(l.priceUsd), q = decimal(l.size);
        if (Number(p) <= 0 || Number(p) >= 1 || Number(q) <= 0) return fail();
        return { priceUsd: p, size: q };
      });
      // Best first and one row per price: anything else is not this contract.
      if (bids.some((l, i) => i > 0 && Number(l.priceUsd) >= Number(bids[i - 1].priceUsd))) return fail();
      return { bids, complete: r.complete, truncated: r.truncated };
    };
    return { observedAtMs, timeEvidence, marketSlug, state: b.state as UsMarketBookView['state'],
      bookObservedAtMs, bookExpiresAtMs, yes: side(b.yes), no: side(b.no) };
  }
  async readMarket(marketSlug: string): Promise<UsSelectedMarket> {
    if (!slug(marketSlug)) return fail('US_CLIENT_INVALID_REQUEST');
    const selected = this.selected;
    if (!selected) return fail('US_CLIENT_AUTH_REQUIRED');
    const { body, observedAtMs, timeEvidence } = await this.fetch('/'+marketSlug, selected);
    const scope = { ...selected.scope };
    const row = market(body.market,observedAtMs), r = record(body.rules), d = record(body.display);
    if (row.marketSlug !== marketSlug || row.book.state !== 'current' || r.venue !== 'polymarket_us'
      || r.marketSlug !== marketSlug || r.sport !== row.sport || !hash(r.revision) || typeof r.active !== 'boolean'
      || typeof r.supportsPostOnly !== 'boolean' || !Array.isArray(r.timeInForce) || !r.timeInForce.length
      || r.timeInForce.some(v=>typeof v !== 'string'||!tifs.includes(v)) || new Set(r.timeInForce).size !== r.timeInForce.length) return fail();
    const priceTick=decimal(r.priceTick), minimumQuantity=decimal(r.minimumQuantity), quantityStep=decimal(r.quantityStep);
    if (Number(priceTick)<=0 || Number(priceTick)>=1 || Number(minimumQuantity)<=0 || Number(quantityStep)<=0
      || time(r.observedAtMs)>observedAtMs || time(r.expiresAtMs)<=observedAtMs) return fail();
    return { scope, observedAtMs, timeEvidence, market: { ...row, timeEvidence }, feedGeneration: text(body.feedGeneration,256),
      rules: { venue:'polymarket_us',marketSlug,revision:r.revision,sport:row.sport,active:r.active,
        priceTick,minimumQuantity,quantityStep,timeInForce:r.timeInForce as string[],supportsPostOnly:r.supportsPostOnly,
        observedAtMs:time(r.observedAtMs),expiresAtMs:time(r.expiresAtMs) },
      display: { question:label(d.question,2000),description:label(d.description,16000),yesLabel:label(d.yesLabel),noLabel:label(d.noLabel) } };
  }
}
